import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { getRedirectDestination } from '@/utils/authRedirect';
import { readProcessingRequestRedirect } from '@/services/processingRequestAuthRedirect';

const log = createComponentLogger('OAuthCallback', 'auth');

/**
 * Handles the OAuth callback at /auth/callback(/cookie)?.
 *
 * The flow has three possible entry states:
 *
 *   1. Browser returned from Supabase Auth with tokens in the URL
 *      fragment, `user` still null, `supabase.auth` session not yet written.
 *      → extract tokens, call setSession.
 *
 *   2. Supabase's `detectSessionInUrl` already consumed the fragment (or
 *      `AuthContext.initializeAuth` restored an existing session) before this
 *      component mounted. `user` is already truthy on entry.
 *      → skip extraction, go straight to redirect consumption.
 *
 *   3. We entered case 1, setSession fired SIGNED_IN, AuthContext processed it,
 *      and `user` flipped truthy on a subsequent render.
 *      → redirect consumption fires via the user-dep effect.
 *
 * Redirect consumption is owned by ONE function (`consumeRedirectOnce`),
 * guarded by a single `redirectedRef` so we never double-navigate or drop the
 * stored destination.
 */
export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const processingFragmentError = readProcessingRequestRedirect() !== null
    ? new URLSearchParams(window.location.hash.slice(1)).get('error')
    : null;
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [, setErrorMessage] = useState<string>('');

  // Tracks whether token extraction has been attempted (prevents re-runs).
  const processedRef = useRef(false);
  // Tracks whether the one-shot redirect consumption has already run.
  const redirectedRef = useRef(false);
  // Tracks error-path timeout for cleanup.
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * One-shot consume-once redirect. Safe to call from any entry point —
   * the ref guard ensures exactly one navigation happens.
   */
  const consumeRedirectOnce = useCallback(() => {
    if (redirectedRef.current) {
      log.debug('Redirect already consumed, skipping duplicate call');
      return;
    }
    if (!location.pathname.includes('/auth/callback')) {
      log.debug('Not on callback path, skipping consumption', { path: location.pathname });
      return;
    }
    // Another tab can sign in while this integration's OAuth attempt is cancelled.
    // Keep its return destination for retry instead of treating that as success.
    if ((searchParams.has('error') || processingFragmentError) && readProcessingRequestRedirect() !== null) {
      return;
    }

    redirectedRef.current = true;

    const storedBefore = localStorage.getItem('oauth_redirect');
    const destination = getRedirectDestination({ checkLocalStorage: true });

    log.info('Consuming post-OAuth redirect', {
      hadStoredRedirect: storedBefore !== null,
      resolvedDestination: destination
    });

    setStatus('success');
    navigate(destination, { replace: true });
  }, [location.pathname, navigate, searchParams, processingFragmentError]);

  // Only Processing Requests gets this retry handoff. The regular callback's
  // error behavior and shared return value stay on their existing path.
  useEffect(() => {
    if (status !== 'error' || redirectedRef.current) {
      return;
    }
    const destination = readProcessingRequestRedirect();
    if (destination !== null) {
      redirectedRef.current = true;
      navigate(`/?redirect=${encodeURIComponent(destination)}`, { replace: true });
    }
  }, [status, navigate]);

  // Token extraction + session write. Handles case 1 above (and short-circuits
  // to consume-once for case 2).
  useEffect(() => {
    const handleOAuthCallback = async () => {
      if (processedRef.current) {
        log.debug('Callback extraction already processed');
        return;
      }

      const hasTokensInHash = window.location.hash.includes('access_token');
      const hasCode = !!searchParams.get('code');
      const hasError = !!searchParams.get('error') || !!processingFragmentError;

      log.debug('OAuthCallback entry', {
        hasTokensInHash,
        hasCode,
        hasError,
        userAlreadyTruthy: !!user
      });

      // Case 2: user already truthy on entry (Supabase auto-detect or
      // initializeAuth already restored the session). Consume redirect
      // immediately; no extraction needed.
      if (user && !hasError) {
        processedRef.current = true;
        log.debug('User already authenticated on callback entry — consuming redirect directly');
        consumeRedirectOnce();
        return;
      }

      // No OAuth-related params AND no user yet. Don't mark as processed so
      // the effect can re-run when user flips (case 3 delegates to the
      // auth-ready gate effect below).
      if (!hasTokensInHash && !hasCode && !hasError) {
        log.debug('No OAuth params and no user yet — waiting for auth state');
        return;
      }

      // Mark as processed BEFORE any async work to prevent re-entrancy.
      processedRef.current = true;

      // Error query: land on / after a delay so the user can read the message.
      // Does NOT consume oauth_redirect — that's reserved for successful auth.
      if (hasError) {
        const errorParam = searchParams.get('error') || processingFragmentError;
        const errorDescription = searchParams.get('error_description') ||
          (processingFragmentError ? new URLSearchParams(window.location.hash.slice(1)).get('error_description') : null);
        log.error('OAuth error in callback query', { error: errorParam, errorDescription });
        setStatus('error');
        setErrorMessage(errorDescription || errorParam || 'Unknown error');
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
        return;
      }

      // Hash-token path (implicit flow returned from Supabase Auth).
      if (hasTokensInHash) {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (!accessToken || !refreshToken) {
          log.error('Callback fragment has access_token marker but tokens are incomplete');
          setStatus('error');
          setErrorMessage('Authentication tokens incomplete');
          return;
        }

        try {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            log.error('setSession failed', { message: sessionError.message });
            setStatus('error');
            setErrorMessage(`Authentication failed: ${sessionError.message}`);
            return;
          }

          if (!data.session || !data.user) {
            log.error('setSession returned without session/user');
            setStatus('error');
            setErrorMessage('Failed to create session');
            return;
          }

          log.info('setSession success — awaiting auth-ready gate', {
            provider: data.user.app_metadata?.provider
          });

          // Clear fragment from URL so the access token isn't visible in history.
          window.history.replaceState({}, document.title, window.location.pathname);

          // Don't consume the redirect here directly — AuthContext needs to
          // process SIGNED_IN and populate `user` first so downstream consumers
          // see a valid session. The auth-ready gate effect below fires
          // consumeRedirectOnce when `user` becomes truthy.
        } catch (err) {
          log.error('Exception during setSession', {
            message: err instanceof Error ? err.message : String(err)
          });
          setStatus('error');
          setErrorMessage(`Authentication error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }
    };

    handleOAuthCallback();

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [location.pathname, navigate, searchParams, user, consumeRedirectOnce, processingFragmentError]);

  // Auth-ready gate — fires the consume-once path as soon as AuthContext has
  // `user` populated on the callback path. Independent of `status` so a
  // transient UI flag can't drop the redirect.
  useEffect(() => {
    if (!user) {
return;
}
    if (!location.pathname.includes('/auth/callback')) {
return;
}
    if (status === 'error') {
return;
}

    log.debug('auth-ready gate firing consumption', { status });
    consumeRedirectOnce();
  }, [user, location.pathname, status, consumeRedirectOnce]);

  return null;
};
